import React, { useEffect, useState } from 'react';
import axios from '../lib/axios';
import { Users, Award, AlertCircle, TrendingUp, Plus, Search, BookOpen, CheckCircle, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Staff = () => {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [alerts, setAlerts] = useState([]);
  const [onboardingSummary, setOnboardingSummary] = useState(null);
  const [activeTab, setActiveTab] = useState('staff');

  useEffect(() => {
    fetchStaffData();
  }, []);

  const fetchStaffData = async () => {
    try {
      const [staffRes, alertsRes] = await Promise.all([
        axios.get('/staff'),
        axios.get('/credential-alerts'),
      ]);
      
      setStaff(staffRes.data);
      setAlerts(alertsRes.data);

      // Try to get onboarding summary
      try {
        const onboardingRes = await axios.get('/staff-onboarding-summary');
        setOnboardingSummary(onboardingRes.data);
      } catch (e) {
        console.log('Could not fetch onboarding summary');
      }
    } catch (error) {
      console.error('Error fetching staff data:', error);
      toast.error('Failed to load staff data');
    } finally {
      setLoading(false);
    }
  };

  const filteredStaff = staff.filter(s =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getComplianceColor = (score) => {
    if (score >= 90) return 'text-green-600 bg-green-100';
    if (score >= 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="staff-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Staff Management
          </h1>
          <p className="text-slate-600 mt-1">Track credentials, training, and compliance</p>
        </div>
      </div>

      {/* Alerts Section */}
      {alerts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-amber-900 mb-2">{alerts.length} Credential Alert{alerts.length > 1 ? 's' : ''}</h3>
              <div className="space-y-1">
                {alerts.slice(0, 3).map((alert, idx) => (
                  <p key={idx} className="text-sm text-amber-800">
                    • {alert.title} - {alert.message}
                  </p>
                ))}
                {alerts.length > 3 && (
                  <p className="text-sm text-amber-600 font-medium mt-2">+ {alerts.length - 3} more alerts</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-100 p-6">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-blue-600" />
            <div>
              <p className="text-sm text-slate-600">Total Staff</p>
              <p className="text-2xl font-bold text-slate-900">{staff.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 p-6">
          <div className="flex items-center gap-3">
            <Award className="w-8 h-8 text-green-600" />
            <div>
              <p className="text-sm text-slate-600">Active CDA Candidates</p>
              <p className="text-2xl font-bold text-slate-900">
                {staff.filter(s => s.cda_status === 'in_progress').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 p-6">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-8 h-8 text-amber-600" />
            <div>
              <p className="text-sm text-slate-600">Expiring Credentials</p>
              <p className="text-2xl font-bold text-slate-900">{alerts.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 p-6">
          <div className="flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-emerald-600" />
            <div>
              <p className="text-sm text-slate-600">Onboarding</p>
              <p className="text-2xl font-bold text-slate-900">
                {onboardingSummary ? `${onboardingSummary.completed}/${onboardingSummary.total_staff}` : '0/0'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="staff">All Staff</TabsTrigger>
          <TabsTrigger value="onboarding">
            Onboarding Status
            {onboardingSummary && onboardingSummary.overdue > 0 && (
              <span className="ml-2 px-1.5 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                {onboardingSummary.overdue}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="staff" className="mt-4">
          {/* Search */}
          <div className="relative max-w-md mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="search"
              placeholder="Search staff..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="search-staff-input"
            />
          </div>

          {/* Staff List */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredStaff.map((member) => (
              <div
                key={member.id}
                className="bg-white rounded-xl border border-slate-100 p-6 hover:border-primary/20 hover:shadow-md transition-all group"
                data-testid={`staff-card-${member.id}`}
              >
                <Link to={`/staff/${member.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center group-hover:from-blue-200 group-hover:to-purple-200 transition-colors">
                        <Users className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {member.name}
                        </h3>
                        <p className="text-sm text-slate-600">{member.email}</p>
                      </div>
                    </div>
                  </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Certifications</span>
                <span className="font-medium text-slate-900">{member.certification_count}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Training Hours (YTD)</span>
                <span className="font-medium text-slate-900">{member.training_hours_this_year}h</span>
              </div>
              {member.cda_status && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">CDA Status</span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    member.cda_status === 'certified' ? 'bg-green-100 text-green-700' :
                    member.cda_status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>
                    {member.cda_status.replace('_', ' ')}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between text-sm pt-2 border-t border-slate-100">
                <span className="text-slate-600">TRS Compliance</span>
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${getComplianceColor(member.trs_compliance_score)}`}>
                  {member.trs_compliance_score}%
                </span>
              </div>
            </div>
            </Link>
            <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
              <Link 
                to={`/staff/${member.id}/onboarding`}
                className="flex items-center justify-center gap-2 text-sm text-primary hover:text-primary/80 font-medium"
              >
                <BookOpen className="w-4 h-4" />
                Pre-Service Training
              </Link>
              <Link 
                to={`/staff/${member.id}/cda-training`}
                className="flex items-center justify-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium"
              >
                🎓 CDA & Texas Requirements
              </Link>
            </div>
          </div>
        ))}
      </div>

      {filteredStaff.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No staff found</h3>
          <p className="text-slate-600">
            {searchTerm ? 'Try adjusting your search' : 'No staff members added yet'}
          </p>
        </div>
      )}
        </TabsContent>

        <TabsContent value="onboarding" className="mt-4">
          {/* Onboarding Summary */}
          {onboardingSummary ? (
            <div className="space-y-6">
              {/* Status Overview */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-sm text-green-700">Completed</span>
                  </div>
                  <p className="text-2xl font-bold text-green-900">{onboardingSummary.completed}</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-5 h-5 text-blue-600" />
                    <span className="text-sm text-blue-700">In Progress</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-900">{onboardingSummary.in_progress}</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    <span className="text-sm text-red-700">Overdue</span>
                  </div>
                  <p className="text-2xl font-bold text-red-900">{onboardingSummary.overdue}</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="w-5 h-5 text-slate-600" />
                    <span className="text-sm text-slate-700">Not Started</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{onboardingSummary.not_started}</p>
                </div>
              </div>

              {/* Training Resources */}
              <div className="bg-white rounded-xl border border-slate-100 p-6">
                <h3 className="font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  📚 Free Texas Training Resources
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <a 
                    href="https://childcare.tamu.edu/courses/texas-annual-training/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg border border-emerald-200 hover:bg-emerald-100 transition-colors"
                  >
                    <div>
                      <p className="font-medium text-emerald-900">Texas A&M AgriLife - Free Modules</p>
                      <p className="text-sm text-emerald-700">Annual training courses for childcare providers</p>
                    </div>
                    <span className="text-emerald-600">→</span>
                  </a>
                  <a 
                    href="https://www.childcareed.com/texas"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors"
                  >
                    <div>
                      <p className="font-medium text-blue-900">ChildCareED Texas</p>
                      <p className="text-sm text-blue-700">Pre-service & annual training courses</p>
                    </div>
                    <span className="text-blue-600">→</span>
                  </a>
                </div>
              </div>

              {/* Staff Onboarding List */}
              <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
                <div className="p-4 border-b border-slate-100">
                  <h3 className="font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Staff Onboarding Status
                  </h3>
                </div>
                <div className="divide-y divide-slate-100">
                  {onboardingSummary.staff_details?.map((ob) => (
                    <Link
                      key={ob.staff_id}
                      to={`/staff/${ob.staff_id}/onboarding`}
                      className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          ob.onboarding_status === 'completed' ? 'bg-green-100' :
                          ob.onboarding_status === 'overdue' ? 'bg-red-100' :
                          ob.onboarding_status === 'in_progress' ? 'bg-blue-100' :
                          'bg-slate-100'
                        }`}>
                          {ob.onboarding_status === 'completed' ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : ob.onboarding_status === 'overdue' ? (
                            <AlertCircle className="w-5 h-5 text-red-600" />
                          ) : (
                            <Clock className="w-5 h-5 text-blue-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{ob.staff_name}</p>
                          <p className="text-sm text-slate-600">{ob.staff_email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          ob.onboarding_status === 'completed' ? 'bg-green-100 text-green-700' :
                          ob.onboarding_status === 'overdue' ? 'bg-red-100 text-red-700' :
                          ob.onboarding_status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {ob.onboarding_status === 'completed' && '✓ Complete'}
                          {ob.onboarding_status === 'in_progress' && '⏳ In Progress'}
                          {ob.onboarding_status === 'overdue' && '⚠️ Overdue'}
                          {ob.onboarding_status === 'not_started' && 'Not Started'}
                        </span>
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                          <span>8hr: {ob.preservice_8hr_completed ? '✓' : '○'}</span>
                          <span>16hr: {ob.preservice_16hr_completed ? '✓' : '○'}</span>
                          <span>CPR: {ob.cpr_first_aid_completed ? '✓' : '○'}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                  {(!onboardingSummary.staff_details || onboardingSummary.staff_details.length === 0) && (
                    <div className="p-8 text-center text-slate-500">
                      No onboarding records found. Create one from a staff member&apos;s profile.
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Onboarding Data</h3>
              <p className="text-slate-600">
                Create onboarding records for staff members to track their pre-service training.
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Staff;
